import { useEffect, useMemo, useState } from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { canTakeTest } from '@pages/Profile/AppropriatenessTest/AppropriatenessTestPage'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductReadContext, useProductWriteContext } from '@context/ProductContext'

import { isTickmillPartner } from '@utils/companyName.utils'
import { navigateBasedOnProductType } from '@utils/path'

import { AppropriatenessTestTakeTestFormValues } from '@model/Test'
import { TickmillProductType } from '@model/TickmillProductType'

import { useAccountAppTestFetch } from './useAccountAppTestFetch'
import { useProductAppTestMutate } from './useProductAppTestMutate'

export const useProductAppropriatenessTest = () => {
  const navigate = useNavigate()

  const { isDefaultCFDProductType, product } = useProductReadContext()
  const { changeProductContext } = useProductWriteContext()
  const { account } = useAccountReadContext()

  const [formValues, setFormValues] = useState<AppropriatenessTestTakeTestFormValues>()
  const [step, setStep] = useState('')
  const [registerProductStep, setRegisterProductStep] = useState(0)
  const [exitConfirmationModal, setExitConfirmationModal] = useState(false)
  const [alreadyUploadedModal, setAlreadyUploadedModal] = useState(false)
  const [riskWarningModalVisible, setRiskWarningModalVisible] = useState(false)

  const { accountAppTest, accountAppTestAnswers, isLoading } = useAccountAppTestFetch()
  const mutation = useProductAppTestMutate()

  const isCFDProduct = isDefaultCFDProductType()
  const isPartner = isTickmillPartner(account)

  const isAllTestsPresent = useMemo(() => {
    const isCFDTestsExists = !!accountAppTest?.sections?.find(
      (appTest) => appTest?.code === 'cfd_investment_knowledge'
    )

    const isETDTestsExists = !!accountAppTest?.sections?.find(
      (appTest) => appTest?.code === 'futures_investment_knowledge'
    )

    return isCFDTestsExists && isETDTestsExists
  }, [accountAppTest?.sections])

  const defaultTestSections = isAllTestsPresent
    ? accountAppTest?.sections?.filter(
        (appTest) => appTest?.code !== 'futures_investment_knowledge'
      )
    : accountAppTest?.sections

  useEffect(() => {
    const isTakeTestAllowed = canTakeTest(account, accountAppTestAnswers, product)
    if (!isTakeTestAllowed && account && accountAppTestAnswers) {
      navigate('/profile/product-appropriateness-test')
    }

    const [section] = defaultTestSections || []
    setStep(section?.code || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, accountAppTest, accountAppTestAnswers, navigate])

  const handleSubmit = async (values: AppropriatenessTestTakeTestFormValues) => {
    await mutation.mutate(values, setAlreadyUploadedModal)
  }

  const handleNextStep = (values: AppropriatenessTestTakeTestFormValues, callback?: () => void) => {
    const sections = defaultTestSections || []
    const stepIndex = sections.findIndex((x) => x.code === step)
    const section = sections?.[stepIndex + 1]

    const answers = {
      ...values,
      freeAnswers: {
        ...formValues?.freeAnswers,
        ...values.freeAnswers,
      },
      selectedAnswers: {
        ...formValues?.selectedAnswers,
        ...values.selectedAnswers,
      },
    }

    if (section?.code) {
      setRegisterProductStep(stepIndex + 1)
      setStep(section.code)
      setFormValues(answers)
    }

    callback?.()
  }

  const handlePrevStep = (callback?: () => void) => {
    const sections = defaultTestSections || []
    const stepIndex = sections.findIndex((x) => x.code === step)
    const section = sections?.[stepIndex - 1]

    if (section?.code) {
      setRegisterProductStep(stepIndex - 1)
      setStep(section.code)
    }

    callback?.()
  }

  const handleConfirmExit = () => {
    setExitConfirmationModal(false)
    changeProductContext(isCFDProduct ? TickmillProductType.ETD : TickmillProductType.CFD)
    navigateBasedOnProductType(isCFDProduct, navigate, isPartner)
  }

  const closeExitConfirmationModal = () => setExitConfirmationModal(false)

  return {
    isCFDProduct,
    formValues,
    handleConfirmExit,
    closeExitConfirmationModal,
    handlePrevStep,
    handleNextStep,
    handleSubmit,
    mutation,
    accountAppTest,
    accountAppTestAnswers,
    defaultTestSections,
    isLoading:
      isLoading ||
      !!(!canTakeTest(account, accountAppTestAnswers, product) && account && accountAppTestAnswers),
    hasAllTests: isAllTestsPresent,
    step,
    registerProductStep,
    account,
    exitConfirmationModal,
    setExitConfirmationModal,
    alreadyUploadedModal,
    setAlreadyUploadedModal,
    riskWarningModalVisible,
    setRiskWarningModalVisible,
  }
}
